import { useState, useEffect } from 'react';

const BREAKPOINTS = {
  sm:  640,
  md:  768,
  lg:  1024,
  xl:  1280,
  '2xl': 1536,
} as const;

type Breakpoint = keyof typeof BREAKPOINTS;

function getBreakpoint(width: number): Breakpoint {
  if (width >= BREAKPOINTS['2xl']) return '2xl';
  if (width >= BREAKPOINTS.xl)   return 'xl';
  if (width >= BREAKPOINTS.lg)   return 'lg';
  if (width >= BREAKPOINTS.md)   return 'md';
  if (width >= BREAKPOINTS.sm)   return 'sm';
  return 'sm';
}

interface BreakpointState {
  width: number;
  breakpoint: Breakpoint;
  isMobile: boolean;   // < 768px
  isTablet: boolean;   // 768–1023px
  isDesktop: boolean;  // >= 1024px
  isWide: boolean;     // >= 1280px
  sidebarCollapsed: boolean;  // 1024–1279px
  sidebarHidden: boolean;     // < 1024px
}

export function useBreakpoint(): BreakpointState {
  const [state, setState] = useState<BreakpointState>(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
    return computeState(w);
  });

  useEffect(() => {
    function handleResize() {
      setState(computeState(window.innerWidth));
    }

    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return state;
}

function computeState(width: number): BreakpointState {
  return {
    width,
    breakpoint: getBreakpoint(width),
    isMobile:          width < 768,
    isTablet:          width >= 768 && width < 1024,
    isDesktop:         width >= 1024,
    isWide:            width >= 1280,
    sidebarCollapsed:  width >= 1024 && width < 1280,
    sidebarHidden:     width < 1024,
  };
}
